import type {
  AiModelGetPayload,
  AiModelSelect,
  BotGetPayload,
  BotSelect,
  BotUpdateArgs,
  ChildPromptGetPayload,
  ChildPromptSelect,
  PromptGetPayload,
  PromptSelect,
  PromptUpdateArgs,
} from '@erikdakoda/database/types';
import type { IntegrationName } from '@erikdakoda/ai-model/shared/integrations';

import {
  botChildPromptForEditSelect,
  buildPromptForEditNestedSelect,
  PROMPT_FOR_EDIT_CHILD_DEPTH,
} from './shared/promptForEditSelect';

export type { BotOverrides } from '@erikdakoda/database/types';

export const PromptForDownloadSelect = {
  id: true,
  name: true,
  text: true,
} satisfies PromptSelect;

export type PromptForDownload = PromptGetPayload<{
  select: typeof PromptForDownloadSelect;
}>;

export const PromptForListItemSelect = {
  id: true,
  name: true,
  ownerId: true,
  spaceId: true,
  publishedAt: true,
  slug: true,
  private: true,
  description: true,
  text: true,
  group: true,
  handle: true,
  labels: {
    select: {
      id: true,
      labelId: true,
      ord: true,
    },
  },
} satisfies PromptSelect;

export type PromptForListItem = PromptGetPayload<{
  select: typeof PromptForListItemSelect;
}>;

/** Select for AI tool prompt search (minimal listing mask). */
export const PromptForAiToolsSearchSelect = {
  id: true,
  name: true,
  description: true,
  group: true,
  handle: true,
} satisfies PromptSelect;

export type PromptForAiToolsSearch = PromptGetPayload<{
  select: typeof PromptForAiToolsSearchSelect;
}>;

export const BotForListItemSelect = {
  id: true,
  name: true,
  publishedAt: true,
  ownerId: true,
  spaceId: true,
  group: true,
  handle: true,
  slug: true,
  avatarId: true,
  integration: true,
  modelName: true,
  useCase: true,
  serviceTier: true,
  temperature: true,
  reasoningEffort: true,
  verbosity: true,
  toolAccessMode: true,
  private: true,
  labels: {
    select: {
      id: true,
      labelId: true,
      ord: true,
    },
  },
} satisfies BotSelect;

export type BotForListItem = BotGetPayload<{
  select: typeof BotForListItemSelect;
}>;

/** Fields used by sidebar and footer bot list rows (avatar, name, model lines). */
export type BotForListDisplay = Pick<
  BotForListItem,
  | 'id'
  | 'name'
  | 'publishedAt'
  | 'avatarId'
  | 'group'
  | 'handle'
  | 'modelName'
  | 'useCase'
  | 'temperature'
  | 'reasoningEffort'
  | 'verbosity'
  | 'toolAccessMode'
>;

/** Select for AI tool bot search (minimal listing mask). */
export const BotForAiToolsSearchSelect = {
  id: true,
  name: true,
  description: true,
  group: true,
  handle: true,
} satisfies BotSelect;

export type BotForAiToolsSearch = BotGetPayload<{
  select: typeof BotForAiToolsSearchSelect;
}>;

export const PromptForEditSelect = buildPromptForEditNestedSelect(PROMPT_FOR_EDIT_CHILD_DEPTH);

export type PromptForEdit = PromptGetPayload<{
  select: typeof PromptForEditSelect;
}>;

export const ChildPromptForEditSelect = {
  id: true,
  ord: true,
  parentBotId: true,
  parentPromptId: true,
  values: true,
  promptId: true,
  prompt: {
    include: {
      childPrompts: {
        orderBy: {
          ord: 'asc',
        },
        include: {
          prompt: {
            include: {
              childPrompts: {
                orderBy: {
                  ord: 'asc',
                },
                include: {
                  prompt: {
                    include: {
                      childPrompts: {
                        orderBy: {
                          ord: 'asc',
                        },
                        include: { prompt: true },
                      },
                    },
                  },
                },
              },
            },
          },
        },
      },
    },
  },
} satisfies ChildPromptSelect;

export type ChildPromptForEdit = ChildPromptGetPayload<{
  select: typeof ChildPromptForEditSelect;
}>;

export const BotForEditSelect = {
  id: true,
  name: true,
  slug: true,
  createdAt: true,
  updatedAt: true,
  publishedAt: true,
  ownerId: true,
  spaceId: true,
  description: true,
  avatarId: true,
  integration: true,
  group: true,
  handle: true,
  modelName: true,
  useCase: true,
  serviceTier: true,
  maxSteps: true,
  temperature: true,
  reasoningEffort: true,
  verbosity: true,
  toolAccessMode: true,
  systemPromptValues: true,
  outputSchema: true,
  autoStart: true,
  analysisBotGroup: true,
  analysisBotHandle: true,
  analysisSchema: true,
  private: true,
  toolSetId: true,
  childPrompts: botChildPromptForEditSelect,
} satisfies BotSelect;

export type BotForEdit = BotGetPayload<{
  select: typeof BotForEditSelect;
}>;

/** `BotForEdit` plus a computed system prompt string (built from child prompts via `computeBotSystemPrompt`). */
export type BotWithSystemPrompt = BotForEdit & { systemPrompt: string };

export const BotForPromptSelect = {
  id: true,
  childPrompts: botChildPromptForEditSelect,
} satisfies BotSelect;

/** Prompt tree for convo appendPrompt (depth 3, ord ascending). */
export const PromptForPromptSelect = buildPromptForEditNestedSelect(PROMPT_FOR_EDIT_CHILD_DEPTH);

export type BotForPrompt = BotGetPayload<{
  select: typeof BotForPromptSelect;
}>;

export type ParentUpdateArgs =
  | {
      parentType: 'Bot';
      where: BotUpdateArgs['where'];
      data: BotUpdateArgs['data'];
    }
  | {
      parentType: 'Prompt';
      where: PromptUpdateArgs['where'];
      data: PromptUpdateArgs['data'];
    };

export type PromptVariableValues = Record<string, string>;

export const BotForGridSelect = {
  id: true,
  createdAt: true,
  updatedAt: true,
  name: true,
  slug: true,
  publishedAt: true,
  ownerId: true,
  spaceId: true,
  description: true,
  avatarId: true,
  group: true,
  handle: true,
  integration: true,
  modelName: true,
  useCase: true,
  serviceTier: true,
  maxSteps: true,
  temperature: true,
  reasoningEffort: true,
  verbosity: true,
  toolAccessMode: true,
  autoStart: true,
  analysisBotGroup: true,
  analysisBotHandle: true,
  analysisSchema: true,
  private: true,
  systemPromptValues: true,
  outputSchema: true,
  toolSetId: true,
  toolSet: {
    select: {
      id: true,
      name: true,
    },
  },
  childPrompts: {
    orderBy: {
      ord: 'asc',
    },
    select: {
      id: true,
      prompt: {
        select: {
          id: true,
          name: true,
          text: true,
        },
      },
    },
  },
  _count: {
    select: {
      convos: true,
    },
  },
} satisfies BotSelect;

export type BotForGrid = BotGetPayload<{
  select: typeof BotForGridSelect;
}>;

export const PromptForGridSelect = {
  id: true,
  createdAt: true,
  updatedAt: true,
  name: true,
  slug: true,
  publishedAt: true,
  description: true,
  text: true,
  variables: true,
  script: true,
  group: true,
  handle: true,
} satisfies PromptSelect;

export type PromptForGrid = PromptGetPayload<{
  select: typeof PromptForGridSelect;
}>;

/** Select for AI tool AI model search/find (minimal listing mask). */
export const AiModelForAiToolsSearchSelect = {
  id: true,
  modelName: true,
  integration: true,
  label: true,
  description: true,
} satisfies AiModelSelect;

export type AiModelForAiToolsSearch = AiModelGetPayload<{
  select: typeof AiModelForAiToolsSearchSelect;
}>;

/** Select for AI tool readAiModel default edit mask. */
export const AiModelForEditSelect = {
  id: true,
  integration: true,
  modelName: true,
  label: true,
  description: true,
  inContext: true,
  outContext: true,
  dimensions: true,
  inMegaTokCost: true,
  outMegaTokCost: true,
  cachedMegaTokCost: true,
  audioInMegaTokCost: true,
  audioInMinuteCost: true,
  cacheWriteMegaTokCost: true,
  modalities: true,
  features: true,
  endpoints: true,
  benchmarkSlug: true,
  scores: {
    select: {
      id: true,
      reasoningEffort: true,
      useCases: true,
      benchmarkModel: true,
      intelligenceIndex: true,
      codingIndex: true,
      agenticIndex: true,
      longContextIndex: true,
      tokensPerSec: true,
      cursorBenchScore: true,
      costPerTask: true,
      tokensPerTask: true,
      stepsPerTask: true,
      gpqaScore: true,
      updatedFromWebAt: true,
    },
  },
  reasoning: true,
  speed: true,
  intelligenceIndex: true,
  tokensPerSec: true,
  schemaInPrompt: true,
  disabled: true,
  releasedAt: true,
  knowledgeAt: true,
  createdAt: true,
  updatedAt: true,
} satisfies AiModelSelect;

export type AiModelForEdit = AiModelGetPayload<{
  select: typeof AiModelForEditSelect;
}>;

/** Select for the AiModel admin data grid. */
export const AiModelForGridSelect = {
  id: true,
  integration: true,
  modelName: true,
  label: true,
  description: true,
  inContext: true,
  outContext: true,
  dimensions: true,
  inMegaTokCost: true,
  outMegaTokCost: true,
  cachedMegaTokCost: true,
  cacheWriteMegaTokCost: true,
  audioInMegaTokCost: true,
  audioInMinuteCost: true,
  modalities: true,
  features: true,
  endpoints: true,
  benchmarkSlug: true,
  scores: {
    select: {
      id: true,
      reasoningEffort: true,
      useCases: true,
      benchmarkModel: true,
      intelligenceIndex: true,
      codingIndex: true,
      agenticIndex: true,
      longContextIndex: true,
      tokensPerSec: true,
      cursorBenchScore: true,
      costPerTask: true,
      tokensPerTask: true,
      stepsPerTask: true,
      gpqaScore: true,
      updatedFromWebAt: true,
    },
  },
  reasoning: true,
  speed: true,
  intelligenceIndex: true,
  tokensPerSec: true,
  schemaInPrompt: true,
  knowledgeAt: true,
  disabled: true,
} satisfies AiModelSelect;

export type AiModelForGrid = AiModelGetPayload<{
  select: typeof AiModelForGridSelect;
}>;

export type ProviderModelCatalogItem = {
  integration: IntegrationName;
  modelName: string;
  label: string;
  description: string;
  inContext: number;
  outContext: number;
  dimensions: number;
  inMegaTokCost: number | null;
  outMegaTokCost: number | null;
  cachedMegaTokCost: number | null;
  modalities: string[];
  features: string[];
  endpoints: string[];
  intelligenceIndex: number | null;
  tokensPerSec: number | null;
  reasoning: number;
  speed: number;
  disabled?: boolean;
  /** Unmodified provider API model object for admin inspection. */
  raw: unknown;
};

/** A tier or prompt-size price collected with an AiModel. */
export type CollectedAiModelPrice = {
  serviceTier: 'default' | 'flex' | 'batch' | 'priority' | 'ultrafast';
  minPromptTokens: number;
  inMegaTokCost: number | null;
  cachedMegaTokCost: number | null;
  cacheWriteMegaTokCost: number | null;
  outMegaTokCost: number | null;
  audioInMegaTokCost: number | null;
  audioInMinuteCost: number | null;
};

/**
 * Scores collected for one model and effort, before they are stored.
 * @property reasoningEffort Effort this row scores.
 * @property benchmarkModel Artificial Analysis entry name, when one matched.
 * @property intelligenceIndex Intelligence index, rounded.
 * @property codingIndex Coding index, rounded.
 * @property agenticIndex Terminal-Bench score times 100, rounded.
 * @property longContextIndex Long-context score times 100, rounded.
 * @property tokensPerSec Median output tokens per second. Null when unknown or 0.
 * @property cursorBenchScore CursorBench score times 100, rounded.
 * @property costPerTask CursorBench dollars per task.
 * @property tokensPerTask CursorBench tokens per task.
 * @property stepsPerTask CursorBench steps per task.
 * @property gpqaScore GPQA Diamond mean times 100, rounded.
 */
export type CollectedAiModelScore = {
  reasoningEffort: 'none' | 'minimal' | 'low' | 'medium' | 'high' | 'xhigh' | 'max';
  benchmarkModel: string | null;
  intelligenceIndex: number | null;
  codingIndex: number | null;
  agenticIndex: number | null;
  longContextIndex: number | null;
  tokensPerSec: number | null;
  cursorBenchScore: number | null;
  costPerTask: number | null;
  tokensPerTask: number | null;
  stepsPerTask: number | null;
  gpqaScore: number | null;
};

/** Full provider metadata collected for Add / Update (extends catalog list rows). */
export type CollectedAiModelData = ProviderModelCatalogItem & {
  audioInMegaTokCost: number | null;
  audioInMinuteCost: number | null;
  cacheWriteMegaTokCost: number | null;
  prices: CollectedAiModelPrice[];
  /** Guessed or stored match key for Artificial Analysis and Epoch. */
  benchmarkSlug: string | null;
  scores: CollectedAiModelScore[];
  releasedAt: string | null;
  knowledgeAt: string | null;
};

export type ProviderModelsResponse = {
  integration: IntegrationName;
  models: ProviderModelCatalogItem[];
  /** Detail requests only: fields `collectAiModelData` could not collect (`models[0]` holds placeholders). */
  unknownFields?: string[];
  fetchedAt: string;
  warning?: string;
};
